import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from '../components/ui/pagination';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Loader2, ShoppingCart, Package } from 'lucide-react';

export default function CartsPage() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCarts, setTotalCarts] = useState(0);
  const pageSize = 30;

  useEffect(() => {
    fetchCarts();
  }, [currentPage]);

  const fetchCarts = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCarts({
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
      });
      const filtered = res.data.filter((c) => c.items?.length > 0);
      setCarts(filtered);
      // Estimate total
      if (filtered.length === pageSize) {
        setTotalCarts(currentPage * pageSize + 1);
      } else {
        setTotalCarts((currentPage - 1) * pageSize + filtered.length);
      }
    } catch (error) {
      toast.error('Failed to load carts');
    } finally {
      setLoading(false);
    }
  };

  const totalValue = carts.reduce((sum, cart) => sum + (cart.total || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="carts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-primary font-semibold">Active Carts</h1>
          <p className="text-muted-foreground mt-1">View customers' shopping carts</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Potential Revenue</p>
          <p className="text-2xl font-heading font-bold text-accent">{formatCurrency(totalValue)}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-base">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Carts</p>
              <p className="text-2xl font-heading font-bold text-primary">{carts.length}</p>
            </div>
            <ShoppingCart className="w-8 h-8 text-primary/20" />
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Items</p>
              <p className="text-2xl font-heading font-bold text-primary">
                {carts.reduce((sum, c) => sum + (c.items?.reduce((s, i) => s + i.quantity, 0) || 0), 0)}
              </p>
            </div>
            <Package className="w-8 h-8 text-primary/20" />
          </CardContent>
        </Card>
      </div>

      {/* Carts Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Cart Value</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No active carts
                    </TableCell>
                  </TableRow>
                ) : (
                  carts.map((cart) => (
                    <TableRow key={cart.id} className="hover:bg-canvas/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-semibold">
                              {cart.customer_name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <span className="font-medium text-primary">{cart.customer_name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="badge-pending">
                          {cart.items?.reduce((s, i) => s + i.quantity, 0) || 0} items
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 max-w-[300px]">
                          {cart.items?.slice(0, 3).map((item, idx) => (
                            <p key={idx} className="text-xs text-muted-foreground truncate">
                              {item.product_name} x{item.quantity}
                            </p>
                          ))}
                          {cart.items?.length > 3 && (
                            <p className="text-xs text-primary">+{cart.items.length - 3} more</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-accent">
                        {formatCurrency(cart.total || 0)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateTime(cart.updated_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && carts.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalCarts)} of ~{totalCarts}{" "}
            carts
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive={true}>
                  {currentPage}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (carts.length === pageSize) setCurrentPage(currentPage + 1);
                  }}
                  className={carts.length < pageSize ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
